import { FC, useState } from 'react';
import { PayoutTxnsData } from '../payouts.model';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { CaretDownGreyIcon, ChevronArrowRightIcon } from 'assets/icons';
import { CTA_TYPES } from '../constants';
import { openInNewTab } from 'utils/commonUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';

interface PayoutTransactionCardProps {
  data: PayoutTxnsData;
}

const PayoutTransactionCard: FC<PayoutTransactionCardProps> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const { rootPage, rootInvoiceId, rootReceiptId, rootDisputeId, rootCustomerId } = useParams();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const hasExpandableContent = (data.ctaName && data.hasAccess) || data?.description;

  const handleExpandClick = () => {
    if (hasExpandableContent) setIsExpanded(prev => !prev);
  };

  const onCtaClick = () => {
    if (data.ctaType === CTA_TYPES.URL) openInNewTab(data.ctaAction);
    else {
      if (rootPage)
        navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}${data.ctaAction}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      else if (rootInvoiceId)
        navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}${data.ctaAction}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      else if (rootReceiptId)
        navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/refund`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      else if (rootDisputeId)
        navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}${data.ctaAction}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      else if (rootCustomerId)
        navigate(`/customers/${currentTeam.id}/${rootCustomerId}${data.ctaAction}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
    }
  };

  return (
    <div className="" key={data.ledgerId}>
      <div className={`${hasExpandableContent ? 'cursor-pointer' : 'pointer-events-none'}`} onClick={handleExpandClick}>
        <div className="pb-3 text-sbase text-primaryText">{data.title}</div>
        <div className="flex items-center">
          <div className="text-17px font-semibold text-primaryText">{data.subTitle}</div>
          <div className="ml-auto text-17px text-primaryText">{getAmountWithCurrency(data.amount)}</div>
          {hasExpandableContent && (
            <div className="ml-1.5">
              <CaretDownGreyIcon className={isExpanded ? 'rotate-180' : ''} />
            </div>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="mt-4">
          <div className="text-sbase text-accentText">{data.description}</div>
          {data.ctaName && data.hasAccess && (
            <div
              role="presentation"
              onClick={onCtaClick}
              className="mt-3 flex w-fit cursor-pointer items-center gap-2 rounded-md bg-secondaryBtn py-1.5 px-4">
              <div className="text-sbase text-secondary">{data.ctaName}</div>
              <div>
                <ChevronArrowRightIcon className="path-fill-current path text-border" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PayoutTransactionCard;
